import { useState, useRef, useCallback, useEffect, type DragEvent, type ChangeEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../api/client';
import { useAppContext } from '../context/AppContext';
import { useToast } from './Toast';
import type { ApiError } from '../types';

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const ACCEPTED_TYPE = 'application/pdf';
const POLL_INTERVAL_MS = 3000;
const MAX_POLL_ATTEMPTS = 60; // 3 minutes max

interface FileUpload {
  id: string;
  file: File;
  status: 'queued' | 'uploading' | 'polling' | 'completed' | 'failed' | 'error';
  songId: string | null;
  songTitle: string | null;
  error: string | null;
  progress: number;
}

function validateFile(file: File): { valid: boolean; error?: string } {
  if (file.type !== ACCEPTED_TYPE) {
    return { valid: false, error: `"${file.name}" is not a PDF.` };
  }
  if (file.size > MAX_FILE_SIZE) {
    return { valid: false, error: `"${file.name}" exceeds 10 MB.` };
  }
  return { valid: true };
}

let fileCounter = 0;

export default function UploadManager() {
  const { addToast } = useToast();
  const { dispatch } = useAppContext();
  const navigate = useNavigate();
  const [uploads, setUploads] = useState<FileUpload[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const pollTimers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const pollCounts = useRef<Map<string, number>>(new Map());

  // Cleanup poll timers on unmount
  useEffect(() => {
    return () => {
      pollTimers.current.forEach((t) => clearTimeout(t));
    };
  }, []);

  const updateUpload = useCallback((id: string, patch: Partial<FileUpload>) => {
    setUploads((prev) => prev.map((u) => (u.id === id ? { ...u, ...patch } : u)));
  }, []);

  const pollForCompletion = useCallback((uploadId: string, songId: string) => {
    const count = (pollCounts.current.get(uploadId) ?? 0) + 1;
    pollCounts.current.set(uploadId, count);

    if (count > MAX_POLL_ATTEMPTS) {
      updateUpload(uploadId, { status: 'completed', songTitle: 'Processing (check library)' });
      return;
    }

    const timer = setTimeout(async () => {
      try {
        const song = await apiClient.getSong(songId);
        if (song.parseStatus === 'completed') {
          updateUpload(uploadId, {
            status: 'completed',
            songTitle: song.title ?? 'Untitled',
            progress: 100,
          });
          dispatch({ type: 'ADD_SONG', payload: song });
          pollTimers.current.delete(uploadId);
        } else if (song.parseStatus === 'failed') {
          updateUpload(uploadId, {
            status: 'failed',
            error: song.parseError ?? 'Parsing failed.',
            songTitle: song.title ?? 'Untitled',
          });
          dispatch({ type: 'ADD_SONG', payload: song });
          pollTimers.current.delete(uploadId);
        } else {
          // Still processing — poll again
          updateUpload(uploadId, { progress: Math.min(90, 50 + count * 2) });
          pollForCompletion(uploadId, songId);
        }
      } catch {
        // Network error during poll — keep trying
        pollForCompletion(uploadId, songId);
      }
    }, POLL_INTERVAL_MS);

    pollTimers.current.set(uploadId, timer);
  }, [updateUpload, dispatch]);

  const processFile = useCallback(async (upload: FileUpload) => {
    const validation = validateFile(upload.file);
    if (!validation.valid) {
      updateUpload(upload.id, { status: 'error', error: validation.error ?? 'Invalid file.', progress: 0 });
      return;
    }

    updateUpload(upload.id, { status: 'uploading', progress: 30 });

    try {
      const response = await apiClient.uploadSong(upload.file);
      updateUpload(upload.id, { status: 'polling', songId: response.songId, progress: 50 });
      pollForCompletion(upload.id, response.songId);
    } catch (err: unknown) {
      const apiErr = err as ApiError;
      const msg = apiErr?.message ?? 'Upload failed.';
      updateUpload(upload.id, { status: 'error', error: msg, progress: 0 });
      addToast(msg, 'error');
    }
  }, [updateUpload, pollForCompletion, addToast]);

  const handleFiles = useCallback((files: FileList | File[]) => {
    const fileArray = Array.from(files).filter((f) => f.type === ACCEPTED_TYPE || f.name.toLowerCase().endsWith('.pdf'));
    if (fileArray.length === 0) {
      addToast('No PDF files found.', 'error');
      return;
    }

    const newUploads: FileUpload[] = fileArray.map((file) => ({
      id: `upload-${++fileCounter}`,
      file,
      status: 'queued' as const,
      songId: null,
      songTitle: null,
      error: null,
      progress: 10,
    }));

    setUploads((prev) => [...newUploads, ...prev]);

    // Process all files
    for (const upload of newUploads) {
      processFile(upload);
    }

    addToast(`Uploading ${fileArray.length} file${fileArray.length > 1 ? 's' : ''}…`, 'info');
  }, [processFile, addToast]);

  const onFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) handleFiles(e.target.files);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const onDragOver = (e: DragEvent) => { e.preventDefault(); setIsDragOver(true); };
  const onDragLeave = (e: DragEvent) => { e.preventDefault(); setIsDragOver(false); };
  const onDrop = (e: DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    if (e.dataTransfer.files.length > 0) handleFiles(e.dataTransfer.files);
  };

  const openFilePicker = () => fileInputRef.current?.click();

  const clearCompleted = useCallback(() => {
    setUploads((prev) => prev.filter((u) => u.status !== 'completed' && u.status !== 'error' && u.status !== 'failed'));
  }, []);

  const allDone = uploads.length > 0 && uploads.every((u) => u.status === 'completed' || u.status === 'error' || u.status === 'failed');
  const completedCount = uploads.filter((u) => u.status === 'completed').length;

  return (
    <div className="page">
      <h2>Upload Songs</h2>

      <div
        className={`upload-dropzone${isDragOver ? ' drag-over' : ''}`}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        onClick={openFilePicker}
        role="button"
        tabIndex={0}
        aria-label="Upload PDF files. Click or drag and drop."
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') openFilePicker(); }}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,application/pdf"
          multiple
          onChange={onFileChange}
          hidden
          aria-hidden="true"
        />
        <p className="upload-dropzone-text">
          Drag &amp; drop PDFs here, or click to browse
        </p>
        <p className="upload-dropzone-hint">PDF files up to 10 MB each — multiple files supported</p>
      </div>

      {uploads.length > 0 && (
        <div className="upload-list" role="status" aria-live="polite">
          {uploads.map((u) => (
            <div key={u.id} className="upload-item">
              <div className="upload-item-header">
                <span className="upload-filename">{u.file.name}</span>
                <span className={`upload-item-status status-${u.status}`}>
                  {u.status === 'queued' && 'Queued'}
                  {u.status === 'uploading' && 'Uploading…'}
                  {u.status === 'polling' && 'Processing…'}
                  {u.status === 'completed' && `✓ ${u.songTitle ?? 'Ready'}`}
                  {u.status === 'failed' && '✗ Parse failed'}
                  {u.status === 'error' && '✗ Error'}
                </span>
              </div>
              {(u.status === 'uploading' || u.status === 'polling' || u.status === 'queued') && (
                <div className="upload-progress-container">
                  <div
                    className="upload-progress-bar"
                    style={{ width: `${u.progress}%` }}
                    role="progressbar"
                    aria-valuenow={u.progress}
                    aria-valuemin={0}
                    aria-valuemax={100}
                  />
                </div>
              )}
              {u.error && <p className="upload-item-error">{u.error}</p>}
            </div>
          ))}

          <div className="upload-actions">
            {allDone && completedCount > 0 && (
              <button className="btn btn-primary" onClick={() => navigate('/songs')} type="button">
                Go to Song Library ({completedCount} ready)
              </button>
            )}
            {allDone && (
              <button className="upload-reset-btn" onClick={clearCompleted} type="button">
                Clear &amp; upload more
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
